import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { formatMoney } from 'accounting-js'
import moment from 'moment'
import { formatHourWithUtc, standardDate } from '../../../../../../../utils/dates'
import { ITEM_STATES, JobItem } from '../../../../domain/items'
import { useTranslation } from 'react-i18next'
import { State } from '@hookstate/core'
import { DECIMALS_MONEY } from '../../../../../../utils/moneyFormat'

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    backgroundColor: '#FFF',
    fontFamily: 'Helvetica',
    paddingTop: 50,
    paddingBottom: 40,
    paddingLeft: 10,
    paddingRight: 10
  },
  row: {
    display: 'flex',
    flex: 0,
    flexDirection: 'row'
  },
  col: {
    flex: 1
  },
  brand: {
    alignItems: 'center',
    flex: 1
  },
  headerCol: {
    display: 'flex',
    flex: 2,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'flex-start'
  },
  headerMore: {
    position: 'absolute',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    fontFamily: 'Helvetica',
    fontSize: 8,
    color: '#000000',
    paddingLeft: 30,
    paddingRight: 30,
    top: 40,
    left: 0,
    right: 0
  },
  textRow: {
    display: 'flex',
    flex: 0,
    flexDirection: 'row',
    marginBottom: 10
  },
  logoImage: {
    width: '70px',
    marginTop: '2px',
    marginBottom: '2px'
  },
  powered: {
    width: '138px',
    marginTop: '13px'
  },
  detailsLabel: {
    fontSize: 10,
    color: '#000000',
    flex: 3,
    marginRight: 5
  },
  detailsText: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
    flex: 4,
    marginRight: 5
  },
  detailsTextLG: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
    flex: 11,
    marginRight: 5
  },
  text: {
    fontSize: 8,
    fontFamily: 'Helvetica',
    color: '#000000'
  },
  textSKU: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#000000'
  },
  smallText: {
    fontSize: 7,
    fontFamily: 'Helvetica',
    color: '#000000'
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8
  },
  center: {
    textAlign: 'center'
  },
  right: {
    textAlign: 'right'
  },
  package: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    margin: 8,
    color: '#000000'
  },
  footer: {
    position: 'absolute',
    display: 'flex',
    bottom: 20,
    left: 0,
    right: 0,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    fontFamily: 'Helvetica',
    fontSize: 8,
    color: '#000000',
    paddingLeft: 25,
    paddingRight: 25
  }
})

type PackageType = {
  department: string
  id: string
  name: string
  reference: string
  state: string
  storeId: string
  transferState: string
  zone: string
}

type ZonePickingPackage = {
  package: PackageType
  categories: {
    [key: string]: JobItem[]
  }
}

type ItemPdfProps = {
  job: any
  items: State<JobItem[]>
  selectedItemCategories: State<string[]>
  selectedItemStates: State<string[]>
  clientLogoURL: string
  categoriesSize: number
  zonePickingPackages: State<ZonePickingPackage[]>
}

const ItemPdf = (props: ItemPdfProps) => {
  const { job, items, selectedItemCategories, selectedItemStates, clientLogoURL, categoriesSize, zonePickingPackages } =
    props
  const { t } = useTranslation()
  const {
    jobNumber,
    origin: { name: originName },
    storeTzCreatedAt,
    receiver: { firstName: clientFirstName, lastName: clientLastName, phoneNumber, email, identification },
    destination: { address, addressTwo },
    paymentMethod,
    paymentValue,
    storeTzSlotFrom,
    storeTzSlotTo,
    logisticCostCurrency
  } = job

  const symbolValue = {
    symbol: `${logisticCostCurrency} `,
    format: '%v %s',
    precision: DECIMALS_MONEY[logisticCostCurrency] || DECIMALS_MONEY.default
  }

  const generatedAt = moment().format('DD/MM/YYYY · LT')
  const hasIdentification =
    (typeof identification === 'string' && identification.replaceAll(' ', '').length > 0) ||
    (identification?.type && identification?.number)
      ? true
      : false

  const itemsCount = zonePickingPackages.get({ noproxy: true }).reduce((sum, { categories, package: _package }) => {
    return (
      sum +
      (categories && Object.keys(categories).length
        ? Object.keys(categories)
            .map((ct) => categories[ct].length)
            .reduce((sum, length) => sum + length)
        : 0)
    )
  }, 0)

  const getItemStateText = (state: string): string => {
    return state === ITEM_STATES.REJECTED || state === ITEM_STATES.CANCELLED ? ITEM_STATES.REJECTED : state
  }

  const getRelatedItem = (item) => {
    const { itemId, isSubstitute, replacedBy } = item

    const itemReplacedBy = replacedBy ? items.get().find(({ itemId: id }) => id === replacedBy) : null
    const itemSubstituteOf = isSubstitute ? items.get().find(({ replacedBy: id }) => id === itemId) : null

    return {
      itemReplacedBy,
      itemSubstituteOf
    }
  }

  const PaintItem = ({ item }) => {
    const {
      name,
      reference,
      attributes: { ean },
      state,
      presentation,
      presentationV2,
      foundQuantity,
      comment
    } = item
    const unitPrice = `${formatMoney(presentationV2 ? presentationV2.price : presentation.price.amount, symbolValue)}`

    const itemQuantity = ` ${foundQuantity} ${t<string>('jobs.job.info.items-view.product-card.from')} ${
      presentationV2
        ? `${presentationV2.quantity} ${presentationV2.unit}`
        : `${presentation.quantity} ${presentation.unit}`
    } `

    const { itemReplacedBy, itemSubstituteOf } = getRelatedItem(item)

    return (
      <View>
        <View style={{ ...styles.row, marginTop: 10, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
          <View style={{ ...styles.col, paddingLeft: 10 }}>
            <Text style={styles.text}>{name}</Text>
            <Text style={styles.textSKU}>{item.attributes.sku}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>{reference}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>{(ean || '').replace(/,/gi, ', ')}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.right }}>{itemQuantity}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.right }}>{unitPrice}</Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.text, ...styles.center }}>
              {t(`jobs.job.info.items-view.print.states.${getItemStateText(state).toLocaleLowerCase()}`)}
            </Text>
          </View>
        </View>
        {itemReplacedBy && itemReplacedBy.name ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={styles.smallText}>{`${t<string>('replacedBy')}: “${itemReplacedBy.name}”`}</Text>
            </View>
          </View>
        ) : null}
        {itemSubstituteOf && itemSubstituteOf.name ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={styles.smallText}>{`${t<string>('replaceTo')}: “${itemSubstituteOf.name}”`}</Text>
            </View>
          </View>
        ) : null}
        {comment ? (
          <View style={{ ...styles.row, marginTop: 3, paddingLeft: 10, paddingRight: 10 }} wrap={false}>
            <View style={{ ...styles.col, paddingLeft: 10 }}>
              <Text style={{ ...styles.text, fontFamily: 'Helvetica-Oblique' }}>{`“${comment}”`}</Text>
            </View>
          </View>
        ) : null}
      </View>
    )
  }

  const PaintCategory = ({ categories, category, marginTop }) => {
    return (
      <>
        <View style={{ ...styles.row, marginTop, paddingLeft: 10, paddingRight: 10 }}>
          <View style={styles.col}>
            <Text
              style={{ ...styles.bold, textTransform: 'capitalize' }}
            >{`${category} (${categories[category].length})`}</Text>
          </View>
        </View>
        {categories[category] &&
          categories[category].map((item, idx) => <PaintItem item={item} key={idx.toString()} />)}
      </>
    )
  }

  return (
    <Document title={jobNumber}>
      <Page size='A4' style={styles.page} wrap>
        <View style={styles.row}>
          <View style={styles.brand}>
            {clientLogoURL !== '' && <Image src={clientLogoURL} style={styles.logoImage} />}
            <Image src='/powered-by.png' style={styles.powered} />
          </View>
          <View style={styles.headerCol}>
            <View style={{ ...styles.textRow, marginBottom: 30 }}>
              <Text style={{ ...styles.detailsLabel, flex: 0 }}>
                {t<string>('jobs.job.info.items-view.print.labels.details')}
              </Text>
              <Text style={{ ...styles.detailsText, flex: 0 }}>{jobNumber}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.origin')}</Text>
              <Text style={styles.detailsText}>{originName}</Text>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.destination')}</Text>
              <Text style={styles.detailsText}>{address}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.createdAt')}</Text>
              <Text style={styles.detailsText}>{`${moment(formatHourWithUtc(standardDate(storeTzCreatedAt))).format(
                'DD/MM/YYYY LT'
              )}`}</Text>
              <Text style={styles.detailsLabel}>
                {t<string>('jobs.job.info.items-view.print.labels.destination-detail')}
              </Text>
              <Text style={styles.detailsText}>{addressTwo}</Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.client')}</Text>
              <Text style={styles.detailsText}>{`${clientFirstName} ${clientLastName}`}</Text>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.delivery')}</Text>
              <Text style={styles.detailsText}>
                {`${moment(formatHourWithUtc(standardDate(storeTzSlotFrom))).format('DD/MM/YYYY LT')} - ${moment(
                  formatHourWithUtc(standardDate(storeTzSlotTo))
                ).format('LT')}`}
              </Text>
            </View>
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.email')}</Text>
              <Text style={styles.detailsTextLG}>{email}</Text>
            </View>
            <View style={styles.textRow}>
              {hasIdentification ? (
                <>
                  <Text style={styles.detailsLabel}>
                    {t<string>('jobs.job.info.items-view.print.labels.identification')}
                  </Text>
                  <Text style={styles.detailsText}>
                    {identification.type && identification.number
                      ? `${identification.type} ${identification.number}`
                      : typeof identification === 'string'
                      ? identification
                      : ''}
                  </Text>
                  <Text style={styles.detailsLabel}>
                    {t<string>('jobs.job.info.items-view.print.labels.payment-method')}
                  </Text>
                  <Text style={styles.detailsText}>{t(`${paymentMethod}`)}</Text>
                </>
              ) : (
                <>
                  <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.phone')}</Text>
                  <Text style={styles.detailsText}>{phoneNumber}</Text>
                  <Text style={styles.detailsLabel}>
                    {t<string>('jobs.job.info.items-view.print.labels.payment-method')}
                  </Text>
                  <Text style={styles.detailsText}>{t(`${paymentMethod}`)}</Text>
                </>
              )}
            </View>
            {hasIdentification ? (
              <View style={styles.textRow}>
                <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.phone')}</Text>
                <Text style={styles.detailsText}>{phoneNumber}</Text>
                <Text style={styles.detailsLabel}>
                  {t<string>('jobs.job.info.items-view.print.labels.payment-value')}
                </Text>
                <Text style={styles.detailsText}>{formatMoney(paymentValue, symbolValue)}</Text>
              </View>
            ) : null}
            <View style={styles.textRow}>
              <Text style={styles.detailsLabel}>{t<string>('jobs.job.info.items-view.print.labels.item-list')}</Text>
              <Text style={styles.detailsText}>{`${itemsCount} ${t<string>(
                'jobs.job.info.items-view.print.printed'
              )}`}</Text>
              {hasIdentification ? (
                <>
                  <Text style={styles.detailsLabel}>
                    {t<string>('jobs.job.info.items-view.print.labels.total-items')}
                  </Text>
                  <Text style={styles.detailsText}>{`${items.get().length} ${t(
                    'jobs.job.info.items-view.print.items-label'
                  )}`}</Text>
                </>
              ) : (
                <>
                  <Text style={styles.detailsLabel}>
                    {t<string>('jobs.job.info.items-view.print.labels.payment-value')}
                  </Text>
                  <Text style={styles.detailsText}>{formatMoney(paymentValue, symbolValue)}</Text>
                </>
              )}
            </View>
            {!hasIdentification ? (
              <View style={styles.textRow}>
                <Text style={styles.detailsLabel}>
                  {t<string>('jobs.job.info.items-view.print.labels.total-items')}
                </Text>
                <Text style={styles.detailsTextLG}>{`${items.get().length} ${t(
                  'jobs.job.info.items-view.print.items-label'
                )}`}</Text>
              </View>
            ) : null}
          </View>
        </View>

        {selectedItemCategories.get().length && categoriesSize > selectedItemCategories.get().length ? (
          <View
            style={{
              ...styles.row,
              marginTop: 25,
              paddingLeft: 10,
              paddingRight: 10
            }}
          >
            <View style={{ ...styles.col, maxWidth: '58px' }}>
              <Text style={{ ...styles.text, fontSize: 10 }}>{`${t(
                'jobs.job.info.items-view.print.categories-label'
              )} · `}</Text>
            </View>
            <View style={{ ...styles.col }}>
              <Text style={{ ...styles.text, ...styles.bold, fontSize: 10 }}>
                {selectedItemCategories.get().join('; ')}
              </Text>
            </View>
          </View>
        ) : (
          <View />
        )}

        {selectedItemStates.get().length ? (
          <View
            style={{
              ...styles.row,
              marginTop: 10,
              paddingLeft: 10,
              paddingRight: 10
            }}
          >
            <View style={{ ...styles.col, maxWidth: '110px' }}>
              <Text style={{ ...styles.text, fontSize: 10 }}>{`${t(
                'jobs.job.info.items-view.print.state-label'
              )} · `}</Text>
            </View>
            <View style={{ ...styles.col }}>
              <Text style={{ ...styles.text, ...styles.bold, fontSize: 10 }}>
                {selectedItemStates
                  .get()
                  .map((state) => t(`jobs.job.info.items-view.${state.toLocaleLowerCase()}`))
                  .join('; ')}
              </Text>
            </View>
          </View>
        ) : (
          <View />
        )}

        <View
          style={{
            ...styles.row,
            marginTop: 25,
            paddingLeft: 10,
            paddingRight: 10
          }}
          fixed
        >
          <View style={{ ...styles.col, paddingLeft: 10 }}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.item')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.reference')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.ean')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.quantity')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.unit-price')}
            </Text>
          </View>
          <View style={styles.col}>
            <Text style={{ ...styles.bold, ...styles.center }}>
              {t<string>('jobs.job.info.items-view.print.labels.state')}
            </Text>
          </View>
        </View>
        {zonePickingPackages.get({ noproxy: true }).map(({ categories, package: _package }, packageIdx) => {
          return (
            <View key={`package-${packageIdx}`}>
              {_package && Object.keys(categories).length > 0 ? (
                <View style={{ backgroundColor: '#F9F9F9', marginTop: 8 }}>
                  <Text style={styles.package}>
                    {t<string>('jobs.job.info.items-view.print.labels.package', _package)}
                  </Text>
                </View>
              ) : null}
              {Object.keys(categories).map((key, idx) => (
                <PaintCategory
                  categories={categories}
                  category={key}
                  key={idx.toString()}
                  marginTop={_package ? 8 : 15}
                />
              ))}
            </View>
          )
        })}

        <View style={styles.footer} fixed>
          <Text>{`${t<string>('jobs.job.info.items-view.print.generated-at')}: ${generatedAt}`}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} ${t<string>('jobs.job.info.items-view.product-card.from')} ${totalPages}`
            }
            fixed
          />
        </View>

        <View
          style={{ ...styles.row, ...styles.headerMore }}
          fixed
          render={({ pageNumber }) => {
            if (pageNumber === 1) {
              return
            }

            return (
              <View style={{ ...styles.textRow }}>
                <Text style={{ ...styles.detailsLabel, flex: 0 }}>Detalle de la orden:</Text>
                <Text style={{ ...styles.detailsText, flex: 0 }}>{jobNumber}</Text>
              </View>
            )
          }}
        />
      </Page>
    </Document>
  )
}

export { ItemPdf }
